'use client'

// Sign-in page — two-column layout: left = auth form, right = visual panel.
// BL-787B (WorkOS → Better Auth cutover): the three sign-in paths — email +
// password, social OAuth (Google, Apple — BL-2776), and email-OTP magic code — all go through Better
// Auth's `authClient` (`@/lib/auth/client`); Google starts through
// `startSocialSignIn`, which hands off to the system browser in the native app
// (BL-2090). The former WorkOS custom-auth
// routes (`/api/auth/login`, `/api/auth/password`) are dormant (deleted in
// BL-788), and the phone-MFA post-login gate was dropped (decision, BL-787B).

import { useState, useEffect, Suspense } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { Eye, EyeOff, ArrowLeft } from 'lucide-react'
import { AuctogonLogo, OTPInput, Spinner } from '@auctogon/ui/web'
import { safeReturnUrl } from '@auctogon/core/validation/safe-return-url'
import {
  EMAIL_NOT_VERIFIED_MESSAGE,
  NO_STAFF_ACCESS_CODE,
  NO_STAFF_ACCESS_MESSAGE,
  SOCIAL_ACCOUNT_NOT_LINKED_CODE,
  SOCIAL_CREATE_DENIED_CODE,
} from '@auctogon/core-admin/auth/staff-sign-in-errors'
import { api } from '@auctogon/admin-frontend/api-client/typed-client'
import { authClient } from '@/lib/auth/client'
import { startSocialSignIn, type SocialProvider } from '@/lib/auth/start-social-sign-in'
import { useSocialProviders } from '@auctogon/admin-frontend/hooks/useSocialProviders'
import { SocialSignInButton, SocialSignInPlaceholder } from '../components/SocialSignInButton'
import { SOCIAL_PROVIDER_IDS } from '@auctogon/core-admin/auth/social-providers'
import Alert from '@auctogon/admin-frontend/components/ui/Alert'

// Error helper text color for the INLINE field errors (per Figma 1586:27568).
// The root banner is the shared Alert (BL-1521) and does not read this.
const ERROR_TEXT = '#F04438'

// The primary brand token. Referenced as a CSS var rather than the raw hex so
// these inline-style sites track `--primary` like every className-based site
// does (design-system-enforcement.md touch=migrate — BL-1127).
const BRAND = 'var(--primary)'

type LoadingAction = 'google' | 'apple' | 'password' | 'otp-send' | 'otp-verify'

/** The code rejections Better Auth's email-OTP sign-in answers with. */
const CODE_REJECTIONS = new Set(['INVALID_OTP', 'OTP_EXPIRED', 'TOO_MANY_ATTEMPTS'])

/**
 * Words for a failed email-code sign-in, and where they go: under the code
 * cells for an answer about the code or the email, the root banner for a server
 * failure (ADR 016 — Form Validation). Only a real code rejection says the code
 * is wrong (BUG-2104).
 *
 * @param error - Better Auth's client error.
 * @returns The message and the surface it belongs on.
 */
function describeOtpSignInFailure(error: { status?: number; code?: string }): {
  on: 'code' | 'banner'
  message: string
} {
  if (error.code === NO_STAFF_ACCESS_CODE) return { on: 'code', message: NO_STAFF_ACCESS_MESSAGE }
  if (error.code != null && CODE_REJECTIONS.has(error.code)) {
    return { on: 'code', message: 'That code is invalid or expired. Request a new one.' }
  }
  if (error.status === 429) return { on: 'code', message: 'Too many attempts. Wait a minute, then try again.' }
  return { on: 'banner', message: 'Something went wrong. Please try again.' }
}

/**
 * The banner for a `?error=` the page was opened with. Better Auth's refusal to
 * create a staff user for an uninvited social account gets the same no-access
 * words the email-code path shows (BUG-2162). Any other error shows as given.
 *
 * @param errorParam - the `error` search param, or null.
 * @returns The banner text, or null when there is no error.
 */
function describeErrorParam(errorParam: string | null): string | null {
  if (errorParam === SOCIAL_CREATE_DENIED_CODE) return NO_STAFF_ACCESS_MESSAGE
  if (errorParam === SOCIAL_ACCOUNT_NOT_LINKED_CODE) return EMAIL_NOT_VERIFIED_MESSAGE
  return errorParam || null
}

function SignInForm() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const returnUrl = searchParams.get('returnUrl') || '/'
  const errorParam = searchParams.get('error')

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loadingAction, setLoadingAction] = useState<LoadingAction | null>(null)
  const [checkingSession, setCheckingSession] = useState(true)
  // Errors: emailError / passwordError are per-field; formError is the
  // form-level banner (URL ?error=, transport failures). otpError is the
  // magic-code step's inline error.
  const [emailError, setEmailError] = useState<string | null>(null)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(describeErrorParam(errorParam))
  // Magic-code (email OTP) step.
  const [otpMode, setOtpMode] = useState(false)
  const [otpValue, setOtpValue] = useState<string[]>(Array(6).fill(''))
  const [otpError, setOtpError] = useState<string | null>(null)

  const clearErrors = () => {
    setEmailError(null)
    setPasswordError(null)
    setFormError(null)
    setOtpError(null)
  }

  // Apple shows only when the server lists it; Google is the long-standing
  // button and stays put whether or not this read succeeds (BL-2776).
  const { providers, loading: providersLoading } = useSocialProviders()
  // Google always shows; Apple only when listed.
  const shownProviders: string[] = ['google', ...providers]

  const canSubmitPassword = email.trim().length > 0 && password.length > 0

  // Redirect if already signed in (hits /api/auth/me → Better Auth session).
  useEffect(() => {
    const checkSession = async () => {
      try {
        const res = await api.auth.me.get.raw()
        if (res.ok) {
          const data = await res.json()
          if (data.user) {
            router.replace(safeReturnUrl(returnUrl))
            return
          }
        }
      } catch {
        // Not authenticated, continue
      }
      setCheckingSession(false)
    }
    checkSession()
  }, [router, returnUrl])

  const handleSocialSignIn = async (provider: SocialProvider) => {
    setLoadingAction(provider)
    clearErrors()
    try {
      const result = await startSocialSignIn({
        provider,
        callbackURL: safeReturnUrl(returnUrl),
        // A refused social account comes back here with ?error= (BUG-2162).
        errorCallbackURL: `/auth/sign-in?returnUrl=${encodeURIComponent(safeReturnUrl(returnUrl))}`,
      })
      // `redirecting` keeps the spinner while the page navigates away; a closed
      // app sheet (`cancelled`) just re-enables the button.
      if (result.status === 'cancelled') {
        setLoadingAction(null)
      } else if (result.status === 'no_access') {
        setFormError(NO_STAFF_ACCESS_MESSAGE)
        setLoadingAction(null)
      } else if (result.status === 'error') {
        setFormError(result.message ?? 'Failed to initiate login')
        setLoadingAction(null)
      }
    } catch {
      setFormError('Something went wrong. Please try again.')
      setLoadingAction(null)
    }
  }

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    clearErrors()
    if (!email) {
      setEmailError('Please enter your email address')
      return
    }
    if (!password) {
      setPasswordError('Please enter your password')
      return
    }
    setLoadingAction('password')
    try {
      const result = await authClient.signIn.email({
        email: email.trim().toLowerCase(),
        password,
      })
      if (result?.error) {
        // Generic message regardless of cause — never reveal which credential
        // is wrong (user-enumeration oracle). The real reason lands in logs.
        setPasswordError('Invalid email or password')
        setLoadingAction(null)
        return
      }
      router.replace(safeReturnUrl(returnUrl))
    } catch {
      setFormError('Something went wrong. Please try again.')
      setLoadingAction(null)
    }
  }

  const handleSendOtp = async () => {
    clearErrors()
    if (!email.trim()) {
      setEmailError('Enter your email to get a sign-in code')
      return
    }
    setLoadingAction('otp-send')
    try {
      const result = await authClient.emailOtp.sendVerificationOtp({
        email: email.trim().toLowerCase(),
        type: 'sign-in',
      })
      if (result?.error) {
        setFormError(result.error.message ?? 'Could not send a code. Please try again.')
        setLoadingAction(null)
        return
      }
      setOtpValue(Array(6).fill(''))
      setOtpMode(true)
      setLoadingAction(null)
    } catch {
      setFormError('Something went wrong. Please try again.')
      setLoadingAction(null)
    }
  }

  const handleVerifyOtp = async (code: string) => {
    clearErrors()
    setLoadingAction('otp-verify')
    try {
      const result = await authClient.signIn.emailOtp({
        email: email.trim().toLowerCase(),
        otp: code,
      })
      if (result?.error) {
        const failure = describeOtpSignInFailure(result.error)
        if (failure.on === 'code') setOtpError(failure.message)
        else setFormError(failure.message)
        setLoadingAction(null)
        return
      }
      router.replace(safeReturnUrl(returnUrl))
    } catch {
      setFormError('Something went wrong. Please try again.')
      setLoadingAction(null)
    }
  }

  if (checkingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white text-primary">
        <Spinner size="lg" />
      </div>
    )
  }

  const banner = formError ? (
    <Alert kind="danger" variant="classic" className="mt-6">
      {formError}
    </Alert>
  ) : null

  return (
    <div className="min-h-screen flex bg-white">
      {/* LEFT — auth form. */}
      <div className="flex-1 flex flex-col items-center px-6 pt-[20vh] lg:px-12">
        <div className="w-full max-w-[400px]">
          {/* Header — logo + wordmark + tagline */}
          <div className="flex items-center gap-2">
            <AuctogonLogo height={33} title="Auctogon" className="text-primary" />
          </div>
          <p className="mt-2 text-lg text-gray-500">
            The operating system for auction houses.
          </p>

          {otpMode ? (
            /* ---------- Magic-code step ---------- */
            <div className="mt-6">
              <button
                type="button"
                onClick={() => { setOtpMode(false); clearErrors() }}
                className="flex items-center gap-1.5 text-sm font-semibold hover:underline"
                style={{ color: BRAND }}
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <p className="mt-4 text-base text-gray-900 font-semibold">Enter your sign-in code</p>
              <p className="mt-1 text-sm text-gray-500">
                We emailed a 6-digit code to {email.trim().toLowerCase()}.
              </p>
              <div className="mt-4">
                <OTPInput
                  value={otpValue}
                  onChange={setOtpValue}
                  onComplete={handleVerifyOtp}
                  disabled={loadingAction === 'otp-verify'}
                  autoFocus
                  brandColor={BRAND}
                  invalid={!!otpError}
                  ariaLabel="Sign-in code"
                />
              </div>
              {otpError && (
                <p className="mt-3 text-sm text-center" style={{ color: ERROR_TEXT }}>{otpError}</p>
              )}
              {banner}
              {loadingAction === 'otp-verify' && (
                <div className="mt-4 flex justify-center text-primary">
                  <Spinner size="sm" />
                </div>
              )}
              <button
                type="button"
                onClick={handleSendOtp}
                disabled={loadingAction !== null}
                className="mt-4 text-sm font-semibold hover:underline disabled:opacity-50"
                style={{ color: BRAND }}
              >
                {loadingAction === 'otp-send' ? 'Sending…' : 'Resend code'}
              </button>
            </div>
          ) : (
            /* ---------- Credentials step ---------- */
            <div className="mt-6">
              <form onSubmit={handlePasswordLogin}>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  autoComplete="email"
                  className={`w-full !bg-[#F7F7F7] border rounded-[10px] px-3.5 py-2.5 text-base text-gray-900 placeholder:text-[#858585] focus:outline-none transition-colors ${
                    emailError ? 'border-[#FDA29B]' : 'border-gray-200 focus:border-gray-400'
                  }`}
                />
                {emailError && (
                  <p className="mt-1.5 text-sm" style={{ color: ERROR_TEXT }}>{emailError}</p>
                )}

                <div className="relative mt-4">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    className={`w-full !bg-[#F7F7F7] border rounded-[10px] px-3.5 py-2.5 pr-11 text-base text-gray-900 placeholder:text-[#858585] focus:outline-none transition-colors ${
                      passwordError ? 'border-[#FDA29B]' : 'border-gray-200 focus:border-gray-400'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 transition-transform active:scale-90 hover:text-gray-600"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {passwordError && (
                  <p className="mt-1.5 text-sm" style={{ color: ERROR_TEXT }}>{passwordError}</p>
                )}

                <div className="mt-2 flex items-center justify-between">
                  <a
                    href="/auth/forgot-password"
                    className="text-sm font-semibold hover:underline"
                    style={{ color: BRAND }}
                  >
                    Forgot your password?
                  </a>
                  <button
                    type="button"
                    onClick={handleSendOtp}
                    disabled={loadingAction !== null}
                    className="text-sm font-semibold hover:underline disabled:opacity-50"
                    style={{ color: BRAND }}
                  >
                    {loadingAction === 'otp-send' ? 'Sending…' : 'Email me a code'}
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={loadingAction !== null || !canSubmitPassword}
                  className="mt-6 w-full py-3 rounded-[10px] text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition active:scale-[0.98] flex items-center justify-center gap-2"
                  style={{ backgroundColor: BRAND }}
                >
                  {loadingAction === 'password' ? (
                    <>
                      <Spinner size="sm" decorative />
                      Logging in...
                    </>
                  ) : (
                    'Log in'
                  )}
                </button>
                {banner}
              </form>

              {/* OR divider */}
              <div className="flex items-center my-6">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="px-3 text-sm text-gray-500">OR</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>

              {SOCIAL_PROVIDER_IDS.map((provider) => {
                if (provider === 'apple' && providersLoading) {
                  return <SocialSignInPlaceholder key={provider} className="mt-3" />
                }
                if (!shownProviders.includes(provider)) return null
                return (
                  <SocialSignInButton
                    key={provider}
                    provider={provider}
                    variant="signIn"
                    className={provider === 'apple' ? 'mt-3' : ''}
                    loading={loadingAction === provider}
                    disabled={loadingAction !== null}
                    onClick={() => handleSocialSignIn(provider)}
                  />
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* RIGHT — hero image (hidden < lg). */}
      <div
        className="hidden lg:block flex-1 bg-black bg-no-repeat"
        aria-hidden="true"
        style={{
          backgroundImage: 'url(/auth/sign-in-hero.png)',
          backgroundSize: '277%',
          backgroundPosition: '65% 50%'
        }}
      />
    </div>
  )
}

function SignInLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white text-primary">
      <Spinner size="lg" />
    </div>
  )
}

export default function SignInPage() {
  return (
    <Suspense fallback={<SignInLoading />}>
      <SignInForm />
    </Suspense>
  )
}
